import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import Icon from '@components/icon/Icons'
import MonthSelector from './MonthSelector'

import useI18n from '@i18n/useI18n'

import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  getDay,
  getDaysInMonth,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfMonth,
  subMonths,
} from 'date-fns'

interface DateRange {
  start: Date
  end: Date
}

interface Props {
  date?: Date
  onDateSelected: (val: Date) => void
  range?: DateRange
  min?: Date
  max?: Date
}

const NOW = Date.now()
const WEEK_DAYS = [1, 2, 3, 4, 5, 6, 0] as const

const Calendar = ({ date, onDateSelected, range, min, max }: Props) => {
  const i18n = useI18n()
  const [month, setMonth] = React.useState(startOfMonth(NOW))
  const [cellSize, setCellSize] = React.useState(0)

  const startOffset = WEEK_DAYS.findIndex((wd) => wd === getDay(month))

  // On calcule combien de jours du mois suivant il faut pour compléter la dernière semaine
  const endOffset = (WEEK_DAYS.length - getDay(endOfMonth(month))) % WEEK_DAYS.length

  const DATES = new Array(startOffset + getDaysInMonth(month) + endOffset)
    .fill(0)
    .map((z, idx) => idx - startOffset)
    .map((i) => addDays(month, i))

  const startMonthIdx = React.useMemo(() => getDay(month), [month])

  const DAYS = React.useMemo(() => WEEK_DAYS.map((d) => addDays(month, d - startMonthIdx)), [month, startMonthIdx])

  const WEEKS_NUMBER = React.useMemo(() => Math.ceil(DATES.length / WEEK_DAYS.length), [DATES])

  const WEEKS = React.useMemo(
    () =>
      Array.from(new Array(WEEKS_NUMBER)).map((_, week) =>
        DATES.slice(week * WEEK_DAYS.length, (week + 1) * WEEK_DAYS.length)
      ),
    [DATES]
  )

  const SELECTION = React.useMemo(() => {
    return WEEKS.map((week) =>
      week.map((day) => {
        if (isSameMonth(day, month)) {
          if (range && (isBefore(day, range.end) || isSameDay(day, range.end))) {
            if (
              isSameDay(day, range.end) ||
              isSameDay(day, range.start) ||
              (isBefore(day, range.end) && isAfter(day, range.start))
            ) {
              return day
            }
          }

          if (date && isSameDay(day, date)) {
            return day
          }
        }

        return undefined
      })
    )
  }, [WEEKS, date, range])

  const renderDay = (day: Date, idx: number) => {
    return (
      <Cell
        key={day.toISOString()}
        onLayout={(evt) => {
          if (!cellSize && idx === 0) {
            setCellSize(evt.nativeEvent.layout.width)
          }
        }}>
        <DayText>{format(day, 'eee', { locale: i18n.locales[i18n.lang] })}</DayText>
      </Cell>
    )
  }

  const renderSelection = () => {
    return SELECTION.map((week, weekIdx) => {
      const start = week.findIndex(Boolean)
      const actives = week.filter(Boolean)

      if (start >= 0) {
        return (
          <Selection
            key={`week_${weekIdx}_${start}`}
            week={weekIdx}
            day={start}
            cell={cellSize}
            size={actives.length}
          />
        )
      }
    })
  }

  const renderDate = (date: Date) => {
    const selectable = isSameMonth(date, month)
    const active = (!min || isAfter(date, min)) && (!max || isBefore(date, max))

    if (selectable) {
      return (
        <TouchableCell
          key={`date_${date.toISOString()}`}
          disabled={!active}
          activeOpacity={0.8}
          onPress={() => onDateSelected(date)}>
          <DateText active={active}>{format(date, 'd')}</DateText>
        </TouchableCell>
      )
    }

    return <Cell key={`date_${date.toISOString()}`} />
  }

  const renderWeek = (week: number) => {
    const dates = DATES.slice(week * WEEK_DAYS.length, (week + 1) * WEEK_DAYS.length)
    return <Row key={`week_${week}`}>{dates.map(renderDate)}</Row>
  }

  return (
    <MainContainer>
      <Header>
        <TouchableOpacity activeOpacity={0.8} onPress={() => MonthSelector.open({ month, onMonth: setMonth })}>
          <Row>
            <MonthText>{format(month, 'MMMM yyyy', { locale: i18n.locales[i18n.lang] })}</MonthText>
            <IconContainer isRight>
              <Icon name="arrow-left" size={30} />
            </IconContainer>
          </Row>
        </TouchableOpacity>

        <Row>
          <Action activeOpacity={0.8} onPress={() => setMonth(subMonths(month, 1))}>
            <IconContainer>
              <Icon name="arrow-left" size={30} />
            </IconContainer>
          </Action>
          <Action activeOpacity={0.8} onPress={() => setMonth(addMonths(month, 1))}>
            <IconContainer isRight>
              <Icon name="arrow-left" size={30} />
            </IconContainer>
          </Action>
        </Row>
      </Header>

      <Row>{DAYS.map(renderDay)}</Row>

      <Grid>
        {renderSelection()}
        {Array.from(new Array(WEEKS_NUMBER)).map((_, idx) => renderWeek(idx))}
      </Grid>
    </MainContainer>
  )
}

export default Calendar

const MainContainer = styled(View)`
  max-width: 500px;
`

const Header = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin: 5px 0;
`

const Grid = styled(View)`
  position: relative;
`

const Row = styled(View)`
  flex-direction: row;
  align-items: center;
`

const Cell = styled(View)`
  height: 40px;
  flex: 1;
  align-items: center;
  justify-content: center;
`

const Action = styled(TouchableOpacity)`
  height: 40px;
  align-items: center;
  justify-content: center;
`

const TouchableCell = styled(TouchableOpacity)`
  height: 40px;
  flex: 1;
  align-items: center;
  justify-content: center;
`

const DayText = styled(Text)`
  ${(props) => props.theme.fonts.Callout};
  color: ${(props) => props.theme.colors.secondaryText};
  text-transform: capitalize;
`

const MonthText = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  width: 130px;
  margin-left: 10px;
  text-transform: capitalize;
`

const DateText = styled(Text)<{ active: boolean }>`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => (props.active ? props.theme.colors.primaryText : props.theme.colors.disable)};
`

const Selection = styled(View)<{ week: number; day: number; cell: number; size: number }>`
  position: absolute;
  top: ${(props) => props.week * 40 + 5}px;
  left: ${(props) => props.day * props.cell + props.cell / 2 - 15}px;
  height: 30px;
  width: ${(props) => 30 + props.cell * (props.size - 1)}px;
  border-radius: 30px;
  background-color: ${(props) => props.theme.colors.secondaryActive};
`

const IconContainer = styled(View)<{ isRight?: boolean }>`
  ${(props) => props.isRight && 'transform: rotate(180deg)'};
  margin: 0 10px;
`
